import React, {useEffect, useState} from "react";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CircularProgress from "@mui/material/CircularProgress";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import EditIcon from "@mui/icons-material/Edit";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import FolderIcon from "@mui/icons-material/Folder";
import ContactPageIcon from "@mui/icons-material/ContactPage";
import {ADMIN_HUB_CARDS, QUICK_LINKS_CARDS} from "./meHubConfig";
import {Helmet} from "react-helmet";
import {useNavigate} from "react-router";
import {signOut, sendEmailVerification} from "firebase/auth";
import {useFirebaseState} from "../firebase/firebaseContext";
import {useCheckIfAdmin} from "../firebase/useCheckIfAdmin";
import {useIsInstructorOrAdmin} from "../firebase/useIsInstructorOrAdmin";
import {useMyInstructorProfile} from "./useMyInstructorProfile";
import {InstructorLeadsTable} from "../instructors/InstructorLeadsTable";
import {EditProfile} from "./EditProfile";

const HubNavCard = ({icon: Icon, title, description, path, buttonLabel}) => {
    const navigate = useNavigate();

    return (
        <Card
            elevation={0}
            sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                borderRadius: 3,
                border: "1px solid rgba(40, 82, 56, 0.12)",
                bgcolor: "#FFFFFF",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                    borderColor: "primary.main",
                    boxShadow: "0 6px 20px rgba(40, 82, 56, 0.08)",
                    transform: "translateY(-2px)",
                },
            }}
        >
            <CardActionArea
                onClick={() => navigate(path)}
                sx={{
                    p: {xs: 2.5, sm: 3},
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    height: "100%",
                    "&:focus-visible": {
                        outline: "2px solid",
                        outlineColor: "primary.main",
                        outlineOffset: 2,
                    },
                }}
            >
                <Box sx={{width: "100%"}}>
                    <Box
                        sx={{
                            display: "inline-flex",
                            p: 1.25,
                            borderRadius: 2.5,
                            bgcolor: "rgba(40, 82, 56, 0.08)",
                            color: "primary.main",
                            mb: 2,
                        }}
                    >
                        <Icon sx={{fontSize: 28}} />
                    </Box>
                    <Typography
                        variant="h6"
                        component="h3"
                        sx={{
                            fontWeight: 700,
                            color: "primary.main",
                            fontSize: "1.1rem",
                            mb: 1,
                            letterSpacing: "-0.01em",
                        }}
                    >
                        {title}
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            color: "text.secondary",
                            lineHeight: 1.6,
                            mb: 2,
                        }}
                    >
                        {description}
                    </Typography>
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        color: "primary.main",
                        fontWeight: 700,
                        fontSize: "0.9rem",
                        mt: "auto",
                    }}
                >
                    <span>{buttonLabel || "Åpne"}</span>
                    <ArrowForwardIcon sx={{fontSize: 18}} />
                </Box>
            </CardActionArea>
        </Card>
    );
};

export const Me = () => {
    const {auth, authReady} = useFirebaseState();
    const navigate = useNavigate();
    const isAdmin = useCheckIfAdmin();
    const isInstructor = useIsInstructorOrAdmin();

    const [sendVerificationEmail, setSendVerificationEmail] = useState(false);
    const [verificationEmailSent, setVerificationEmailSent] = useState(false);
    const [editProfile, setEditProfile] = useState(false);

    const {instructor} = useMyInstructorProfile({userId: auth?.currentUser?.uid});

    const isReady = authReady !== false;

    useEffect(() => {
        if (isReady && !auth?.currentUser) {
            navigate("/login");
        }
    }, [auth, isReady, navigate]);

    if (!isReady) {
        return (
            <Container maxWidth="lg" sx={{ py: 10, display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
                <CircularProgress color="primary" />
            </Container>
        );
    }

    if (!auth?.currentUser) {
        return null;
    }

    const user = auth.currentUser;

    const handleSignOut = () => {
        signOut(auth)
            .then(() => {
                navigate("/login");
            })
            .catch(error => console.error({error}));
    };

    const renderVerificationOrEdit = () => {
        if (!user.emailVerified) {
            return (
                <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<PriorityHighIcon />}
                    disabled={verificationEmailSent}
                    onClick={() => {
                        sendEmailVerification(auth.currentUser)
                            .then(() => {
                                setSendVerificationEmail(true);
                                setVerificationEmailSent(true);
                            })
                            .catch(error => {
                                setSendVerificationEmail(true);
                                console.error("Email verification failed", error);
                            });
                    }}
                    sx={{minHeight: 44, textTransform: "none", fontWeight: 600, borderRadius: 2}}
                >
                    Verifiser e-post
                </Button>
            );
        }
        return (
            <Button
                variant="outlined"
                color="primary"
                startIcon={<EditIcon />}
                onClick={() => setEditProfile(true)}
                sx={{minHeight: 44, textTransform: "none", fontWeight: 600, borderRadius: 2}}
            >
                Endre profil
            </Button>
        );
    };

    const getRoleChip = () => {
        if (isAdmin) {
            return (
                <Chip
                    label="Administrator"
                    sx={{
                        fontWeight: 700,
                        bgcolor: "primary.main",
                        color: "common.white",
                    }}
                />
            );
        }
        if (isInstructor) {
            return (
                <Chip
                    label="Sertifisert Kursholder"
                    sx={{
                        fontWeight: 700,
                        bgcolor: "rgba(40, 82, 56, 0.12)",
                        color: "primary.main",
                    }}
                />
            );
        }
        return (
            <Chip
                label="Registrert bruker"
                variant="outlined"
                sx={{
                    fontWeight: 600,
                    borderColor: "rgba(40, 82, 56, 0.2)",
                    color: "text.secondary",
                }}
            />
        );
    };

    return (
        <Box sx={{height: "100%", py: {xs: 2, sm: 3}}}>
            <Helmet>
                <title>Min side - Kurs i bekymringsmestring</title>
            </Helmet>
            <Container maxWidth="lg">
                {/* Profile Header Card */}
                <Paper
                    elevation={0}
                    sx={{
                        p: {xs: 3, sm: 4},
                        mb: 4,
                        borderRadius: 4,
                        bgcolor: "#FFFFFF",
                        border: "1px solid rgba(40, 82, 56, 0.1)",
                        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.03)",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: {xs: "column", sm: "row"},
                            alignItems: {xs: "flex-start", sm: "center"},
                            justifyContent: "space-between",
                            gap: 3,
                        }}
                    >
                        <Box sx={{display: "flex", alignItems: "center", gap: 2.5}}>
                            <Avatar
                                src={user.photoURL}
                                alt={user.displayName || "Bruker"}
                                sx={{
                                    width: {xs: 72, sm: 88},
                                    height: {xs: 72, sm: 88},
                                    border: "3px solid",
                                    borderColor: "rgba(40, 82, 56, 0.15)",
                                }}
                            />
                            <Box>
                                <Box sx={{display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", mb: 0.5}}>
                                    <Typography variant="h5" component="h1" sx={{fontWeight: 700, color: "text.primary"}}>
                                        Velkommen {user.displayName}
                                    </Typography>
                                    {getRoleChip()}
                                </Box>
                                <Typography variant="body2" sx={{color: "text.secondary", wordBreak: "break-all"}}>
                                    E-post: {user.email}
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{display: "flex", gap: 1.5, flexWrap: "wrap"}}>
                            {renderVerificationOrEdit()}
                            <Button
                                variant="outlined"
                                color="inherit"
                                onClick={handleSignOut}
                                startIcon={<ExitToAppIcon />}
                                sx={{
                                    minHeight: 44,
                                    textTransform: "none",
                                    fontWeight: 600,
                                    borderRadius: 2,
                                    borderColor: "divider",
                                    "&:hover": {borderColor: "error.main", color: "error.main"},
                                }}
                            >
                                Logg ut
                            </Button>
                        </Box>
                    </Box>
                </Paper>

                {/* Kursholder Section */}
                {(isInstructor || isAdmin) && (
                    <Box sx={{mb: 5}}>
                        <Box sx={{mb: 2.5}}>
                            <Typography
                                variant="h5"
                                component="h2"
                                sx={{fontWeight: 700, color: "primary.main", letterSpacing: "-0.01em"}}
                            >
                                Kursholder-ressurser
                            </Typography>
                            <Typography variant="body2" sx={{color: "text.secondary"}}>
                                Verktøy og materiell for sertifiserte kursholdere
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: {xs: "1fr", sm: "repeat(2, 1fr)"},
                                gap: 3,
                            }}
                        >
                            <HubNavCard
                                icon={FolderIcon}
                                title="Kursfiler og materiell"
                                description="Last ned og administrer kursmanualer, presentasjoner, lydfiler og materiell til kursene dine."
                                path="/files"
                                buttonLabel="Åpne kursfiler"
                            />
                            <HubNavCard
                                icon={ContactPageIcon}
                                title="Instruktørprofil"
                                description="Rediger din offentlige kursholderprofil med presentasjonstekst, kontaktinfo, fylke og kurstilbud."
                                path="/instructor-profile"
                                buttonLabel="Rediger profil"
                            />
                        </Box>

                        {instructor?.id && (
                            <Box id="kurshenvendelser" sx={{mt: 4}}>
                                <InstructorLeadsTable instructorId={instructor.id} />
                            </Box>
                        )}
                    </Box>
                )}

                {/* Administrator Section */}
                {isAdmin && (
                    <Box sx={{mb: 5}}>
                        <Box sx={{mb: 2.5}}>
                            <Typography
                                variant="h5"
                                component="h2"
                                sx={{fontWeight: 700, color: "primary.main", letterSpacing: "-0.01em"}}
                            >
                                Administratorverktøy
                            </Typography>
                            <Typography variant="body2" sx={{color: "text.secondary"}}>
                                Systemadministrasjon for brukere, kursholdere og e-postutsendelser
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: {xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)"},
                                gap: 3,
                            }}
                        >
                            {ADMIN_HUB_CARDS.map((card) => (
                                <HubNavCard key={card.path} {...card} />
                            ))}
                        </Box>
                    </Box>
                )}

                {/* Hurtiglenker Section */}
                <Box sx={{mb: 5}}>
                    <Box sx={{mb: 2.5}}>
                        <Typography
                            variant="h5"
                            component="h2"
                            sx={{fontWeight: 700, color: "text.primary", letterSpacing: "-0.01em"}}
                        >
                            Nyttige snarveier
                        </Typography>
                        <Typography variant="body2" sx={{color: "text.secondary"}}>
                            Raske snarveier til materiellbestilling og kursholderkartet
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {xs: "1fr", sm: "repeat(2, 1fr)"},
                            gap: 3,
                        }}
                    >
                        {QUICK_LINKS_CARDS.map((card) => (
                            <HubNavCard key={card.path} {...card} />
                        ))}
                    </Box>
                </Box>

                {/* Snackbar feedback */}
                <Snackbar
                    open={sendVerificationEmail}
                    autoHideDuration={6000}
                    onClose={() => setSendVerificationEmail(false)}
                    anchorOrigin={{vertical: "top", horizontal: "right"}}
                >
                    <Alert
                        onClose={() => setSendVerificationEmail(false)}
                        severity="success"
                        variant="filled"
                        sx={{width: "100%"}}
                    >
                        E-post med verifiseringskode er sendt til {user.email}
                    </Alert>
                </Snackbar>

                <EditProfile
                    open={editProfile}
                    onClose={() => setEditProfile(false)}
                />
            </Container>
        </Box>
    );
};